import { MdContentCopy, MdOutlineDescription, MdOutlineEdit } from 'react-icons/md';
import Button from '@srm/ui/button';
import Tooltip from '@srm/ui/tooltip';
import { sendNotification } from '@srm/ui/lib/notifications';
import { COPY_FAILURE_REASON, copyText } from '@srm/ui/lib/clipboard';
import { ITableColumn, TableColumn } from '@models/Forms/IForms';
import { findFormValuesInconsistency, hasFormValuesTableColumn } from '@utils/tableColumnGuards';
import { ITableColumnSource, TableRow } from './tableColumns';
import { IColumnRulesAccess } from './tableRuleHooks';

/** Копирование содержимого колонки в буфер (showCopyColumnContentButton) —
 *  паритет CopyColumnContentButton, значения из строк формы. */
export const CopyColumnButton = ({ column, getRows }: { column: TableColumn; getRows: () => TableRow[] }) => {
    const onClick = () => {
        const result = getRows()
            .map((row) => row[column.key])
            .filter(Boolean)
            .join(column.copyColumnContentSeparator || ',');
        if (!result) {
            sendNotification({ title: 'Нет данных для копирования', variant: 'yellow' }, 1500);
            return;
        }
        void copyText(result, { failure: { title: 'Ошибка копирования', message: COPY_FAILURE_REASON } });
    };
    return (
        <Tooltip
            openDelay={100}
            background="black"
            position="bottom"
            label="Скопировать содержимое столбца в буфер обмена"
        >
            <button
                type="button"
                className="tdn-hbtn"
                aria-label={`Скопировать содержимое столбца: ${column.name}`}
                onClick={onClick}
            >
                <MdContentCopy size="14" />
            </button>
        </Tooltip>
    );
};

/** Карандаш в шапке колонки: применить значение к выбранным строкам (паритет
 *  EditRowMultiButton, но без DOM-детекта readonly — по прекэшу формул).
 *  ВАЖНО: модалку рендерит EditTable (onOpen), не кнопка — шапка грида
 *  ремаунтится (TanStack headers) и локальный state открытости в ней теряется. */
export const RowMultiEditButton = ({
    source,
    getColumnRules,
    getSelectedRows,
    onOpen,
}: {
    source: ITableColumnSource;
    /** Геттер (ref): пересчёт правил не пересобирает шапку колонки. */
    getColumnRules: () => IColumnRulesAccess;
    getSelectedRows: () => TableRow[];
    onOpen: (source: ITableColumnSource, firstSelectedRow: TableRow) => void;
}) => {
    const column = source.column;

    const onClick = () => {
        const selected = getSelectedRows();
        if (selected.length === 0) {
            sendNotification({ message: 'Необходимо выбрать минимум одну строку', variant: 'red' });
            return;
        }
        const staticReadonly = 'readonly' in column && (column as ITableColumn).readonly === 'true';
        const columnRules = getColumnRules();
        const hasReadonly =
            staticReadonly || selected.some((row) => columnRules.peekFor(row, source.columnId)?.readonly === true);
        if (hasReadonly) {
            sendNotification({ message: 'Среди выбранных строк есть строка только для чтения', variant: 'red' });
            return;
        }
        if (hasFormValuesTableColumn(column)) {
            const diff = findFormValuesInconsistency(selected, column);
            if (diff) {
                sendNotification({
                    message: `У выделенных строк различаются следующие значения: ${diff}`,
                    variant: 'red',
                });
                return;
            }
        }
        onOpen(source, selected[0]);
    };

    return (
        <Tooltip
            openDelay={100}
            background="black"
            position="bottom"
            label="Редактировать значение колонки в выбранных строках"
        >
            <button
                type="button"
                className="tdn-hbtn"
                aria-label={`Редактировать значение колонки в выбранных строках: ${column.name}`}
                onClick={onClick}
            >
                <MdOutlineEdit size="14" />
            </button>
        </Tooltip>
    );
};

/** Кнопка входа в единый drawer таблицы. Состояние панели принадлежит EditTable:
 *  строка может быть удалена или добавлена, не размонтируя сам drawer. */
export const RowEditButton = ({ onOpen }: { onOpen: () => void }) => (
    <Tooltip openDelay={300} background="black" position="top" label="Редактировать строку">
        <Button
            variant="text"
            tone="neutral"
            size="auto"
            aria-label="Редактировать строку"
            onClick={onOpen}
            startAdornment={<MdOutlineEdit size="18" />}
        />
    </Tooltip>
);

/** Форма только для чтения плюс проверка подписи строки, если она настроена. */
export const RowViewButton = ({ onOpen }: { onOpen: () => void }) => (
    <Tooltip openDelay={300} background="black" position="top" label="Просмотреть строку">
        <Button
            variant="text"
            tone="neutral"
            size="auto"
            aria-label="Просмотреть строку"
            onClick={onOpen}
            startAdornment={<MdOutlineDescription size="18" />}
        />
    </Tooltip>
);
